import { useState } from 'react';
import { Box, ButtonBase, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';

import { useUserRoles } from 'app/hooks';
import useOrganisationFeatureFlag from 'app/hooks/useOrganisationFeatureFlag';
import useHasPlusFeature from 'app/hooks/useHasPlusFeature';
import useEventTracker from 'app/hooks/useEventTracker';
import { WEBSITE_UPSELL_EVENTS } from 'app/tracking/constants';
import useTodaysVideoCalls from 'app/shared-components/appointment-notification-panel/useTodaysVideoCalls';
import config from '../../../config.json';

const SMS_CAMPAIGNS_BANNER_DISMISSED_KEY = 'smsCampaignsBannerAdDismissed';

const SmsCampaignsBanner = () => {
  const navigate = useNavigate();
  const { isSupport, isPharmacist, isPharmacyAdmin } = useUserRoles();
  const smsCampaignsEnabled = useOrganisationFeatureFlag('sms_campaigns');
  // The banner promotes the Website builder, so it goes with the builder when support hides it.
  const { isHidden: webBuilderHidden, isLoading: featuresLoading } = useHasPlusFeature(
    'webBuilderAccessEnabled',
    false
  );
  const { videoCalls } = useTodaysVideoCalls();
  const trackEvent = useEventTracker();
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(SMS_CAMPAIGNS_BANNER_DISMISSED_KEY) === 'true'
  );

  const shouldSeeBannerByRole = isSupport || isPharmacist || isPharmacyAdmin;

  if (config.deploymentRegion === 'canada') return null;
  if (
    !smsCampaignsEnabled ||
    featuresLoading ||
    webBuilderHidden ||
    !shouldSeeBannerByRole ||
    dismissed ||
    videoCalls.length > 0
  ) {
    return null;
  }

  const handleDismiss = () => {
    localStorage.setItem(SMS_CAMPAIGNS_BANNER_DISMISSED_KEY, 'true');
    setDismissed(true);
  };

  const handleLearnMore = () => {
    trackEvent(WEBSITE_UPSELL_EVENTS.BANNER_CLICKED.action, WEBSITE_UPSELL_EVENTS.BANNER_CLICKED);
    navigate('/plus-websites');
    setDismissed(true);
  };

  return (
    <Box className="flex items-center justify-between px-[25px] py-[10px] bg-featured text-white">
      <ButtonBase
        onClick={handleLearnMore}
        className="flex grow items-center justify-start gap-[10px] text-left text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
      >
        <span className="text-[14px]">
          ✨ <span className="font-bold">Meet the new website builder!</span> A faster, easier way
          to customise and manage your pharmacy website.{' '}
          <span className="font-bold underline">Start here</span>
        </span>
      </ButtonBase>
      <IconButton
        onClick={handleDismiss}
        aria-label="Dismiss banner"
        size="small"
        className="hover:opacity-80 text-white"
      >
        <CloseIcon fontSize="small" className="text-white" />
      </IconButton>
    </Box>
  );
};

export default SmsCampaignsBanner;
